import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";
import { useCopy } from "../../context/CopyContext";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import PoweredBy from "../PoweredBy";

const OfferView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { getCopy } = useCopy();

  const handleNoThanks = () => {
    console.log("User declined offer");
    dispatch({ type: "SET_CLAIMED_OFFER", payload: false });
    // Navigate to final confirmation/thank you screen
    navigate("/confirmation");
  };

  const handleClaimOffer = () => {
    console.log("User claimed offer");
    dispatch({ type: "SET_CLAIMED_OFFER", payload: true });
    // Navigate to final confirmation/thank you screen  
    navigate("/confirmation");
  };

  // Mock product data - in real app this would come from state or API
  const offerProduct = {
    name: "Ultra Facial Cleanser",
    size: "150ml",
    originalPrice: "$52.00",
    discountedPrice: "$41.60",
    discount: "20% OFF!",
    image: "/kiehls-ultra-facial-cleanser.png" // This would be the actual product image
  };

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="164:5036"
    >
      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-20">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={false}
              itemsNumber={state.itemsCount.toString()}
              
              
              
              
              
            />
          </div>
        </div>

        {/* Main Content Area - with 3-row grid */}
        <motion.div 
          className="flex-1 grid grid-rows-3 gap-0 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Row 1 - Main heading */}
          <div className="flex items-center justify-center">
            <div className="text-center w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.6, 
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              >
                <Heading
                  title={getCopy("offer.heading")}
                  subtitle={getCopy("offer.subheading")}
                />
              </motion.div>
            </div>
          </div>

          {/* Rows 2-3 - Product Image and Details */}
          <div className="row-span-2 flex items-center justify-center overflow-hidden">
            <motion.div
              className="w-full flex flex-col items-center justify-center"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ 
                duration: 0.8, 
                delay: 0.2,
                ease: [0.25, 0.1, 0.25, 1]
              }}
            >
              {/* Product Image - Fits remaining space */}
              <div className="flex items-center justify-center w-full">
                <img src={offerProduct.image} alt={offerProduct.name} className="max-w-[60%] object-contain" />
              </div>
              
              {/* Product Details - Compact layout */}
              <motion.div
                className="text-center w-full flex flex-col justify-center h-[25%] py-1 pt-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.6, 
                  delay: 0.4,
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              >
                {/* Product Name */}
                <h2 className="font-brand-secondary font-normal text-[28px] leading-[36px] text-brand-text-primary mb-1">
                  {offerProduct.name}
                </h2>
                
                {/* Product Size */}
                <p className="font-brand-secondary font-normal text-[20px] leading-[26px] text-brand-text-muted mb-8">
                  {offerProduct.size}
                </p>
                
                {/* Price and Discount */}
                <div className="flex flex-row gap-3 items-center justify-center">
                  <span className="font-brand-secondary font-normal text-[40px] leading-[36px] text-brand-text-secondary line-through">
                    {offerProduct.originalPrice}
                  </span>
                  <span className="font-brand-secondary font-semibold text-[40px] leading-[36px] text-brand-text-primary">
                    {offerProduct.discountedPrice}
                  </span>
                  <div className="bg-brand-tertiary rounded-full px-3 py-1">
                    <span className="font-brand-secondary font-medium text-[20px] leading-[26px] text-white">
                      {offerProduct.discount}
                    </span>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>

        {/* Bottom Section - No Thanks/Claim Offer buttons and footer */}
        <div className="flex flex-col gap-12 items-center mx-20 mb-10">
          {/* Buttons */}
          <div className="flex flex-row gap-10 w-full">
            <Button variant="secondary" onClick={handleNoThanks}>
              {getCopy("offer.buttons.secondary")}
            </Button>
            <Button variant="primary" onClick={handleClaimOffer}>
              {getCopy("offer.buttons.primary")}
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferView;
